import React from 'react';
import classNameBind from 'classnames/bind';
import Icon from '../Icon';
import styles from './TextField.module.css';
import Tooltip from '../Tooltip';
import { withTranslation } from '../Translator';

const TEXT_INPUT_TYPE = 'text';
const PASSWORD_INPUT_TYPE = 'password';

class TextField extends React.Component {
  inputRef = React.createRef();

  state = {
    passwordPreviewEnabled: false,
  };

  static defaultProps = {
    value: '',
    disabled: false,
    onKeyDown: () => {},
    onChange: () => {},
    onBlur: () => {},
    onMouseDown: null,
    placeholder: '',
    icon: null,
    error: null,
    type: 'text',
    touched: false,
    units: null,
    autoComplete: 'off',
    autoSelected: false,
    id: null,
    dataId: 'TextField',
    onClear: () => {},
    extraMinWidth: false,
    forceOutline: false,
    passwordPreview: false,
  }

  componentDidMount() {
    if (this.props.autoFocus && this.props.autoSelected) {
      this.focus();
      this.select();
    }
  }

  getValueFromEvent(event) {
    const { type } = this.props;

    if (type === 'number') {
      // eslint-disable-next-line no-restricted-globals
      if (event.target.value === '' || isNaN(event.target.value)) {
        return '';
      }

      return Number(event.target.value);
    }

    return event.target.value;
  }

  focus = () => {
    this.inputRef.current.focus();
  }

  select = () => {
    this.inputRef.current.select();
  }

  handleClear = () => {
    const { onChange, name, onClear } = this.props;

    onChange({
      target: {
        name,
        value: '',
      },
    });

    onClear();
    this.focus();
  }

  handleFocus = () => {
    const { onFocus, autoSelected } = this.props;

    if (autoSelected && this.inputRef.current) {
      this.select();
    }

    if (onFocus) onFocus();
  }

  handleChange = (event) => {
    const { onChange } = this.props;

    onChange({
      target: {
        name: event.target.name,
        value: this.getValueFromEvent(event),
      },
    });
  }

  handleBlur = (event) => {
    const { onBlur } = this.props;

    onBlur({
      target: {
        name: event.target.name,
        value: this.getValueFromEvent(event),
      },
    });
  }

  render() {
    const {
      id,
      name,
      clearable,
      icon,
      disabled,
      placeholder,
      value,
      maxLength,
      autoFocus,
      error,
      onKeyDown,
      type,
      touched,
      autoComplete,
      units,
      dataId,
      onMouseDown,
      extraMinWidth,
      forceOutline,
      appearance,
      passwordPreview,
      t,
    } = this.props;
    const { passwordPreviewEnabled } = this.state;
    const props = {
      id,
      type,
      value,
      maxLength,
      name,
      onChange: this.handleChange,
      onKeyDown,
      onBlur: this.handleBlur,
      onFocus: this.handleFocus,
      onMouseDown,
      placeholder,
      disabled,
      autoFocus,
      autoComplete,
    };

    const bindClasses = classNameBind.bind(styles);
    const className = bindClasses('text-field', { disabled, invalid: error && touched, extraMinWidth, forceOutline }, appearance);
    const isPasswordType = type === PASSWORD_INPUT_TYPE;
    const getInputType = () => {
      if (!isPasswordType) {
        return type;
      }

      if (passwordPreviewEnabled) {
        return TEXT_INPUT_TYPE;
      }

      return PASSWORD_INPUT_TYPE;
    };

    return (
      <div className={className} data-ppuc-id={dataId}>
        {icon && (
          <Icon
            name={icon}
            className={bindClasses('left-icon')}
            color="gray"
            dataId={`${dataId}.icon`}
          />
        )}
        <input
          {...props}
          type={getInputType()}
          ref={this.inputRef}
          className={bindClasses(
            'input',
            'clear-default-input',
            `type-${type}`,
            `${units ? 'withUnits' : ''}`,
            {
              passwordPreview,
              clearable,
            },
          )}
          data-ppuc-id={`${dataId}.input`}
        />
        {units && <span className={bindClasses('units', { clearable })} data-ppuc-id={`${dataId}.unit`}>{units}</span>}
        {Boolean(clearable && value !== '') && (
          // no tabindex needed for working popover
          // eslint-disable-next-line jsx-a11y/interactive-supports-focus
          <span
            className={bindClasses('action-button')}
            role="button"
            onClick={this.handleClear}
            data-ppuc-id={`${dataId}.clear`}
          >
            <Icon name="clear" color="gray" />
          </span>
        )}
        {Boolean(passwordPreview && isPasswordType && value !== '') && (
          // no tabindex needed for working popover
          // eslint-disable-next-line jsx-a11y/interactive-supports-focus
          <span
            className={bindClasses('action-button')}
            role="button"
            data-ppuc-id={`${dataId}.previewPassword`}
            onClick={() => {
              this.setState(state => ({
                ...state,
                passwordPreviewEnabled: !state.passwordPreviewEnabled,
              }));
            }}
          >
            <Tooltip
              content={passwordPreviewEnabled
                ? t('textField.input.password.action.tooltip.hide')
                : t('textField.input.password.action.tooltip.show')
              }
            >
              <div
                className={bindClasses('icon')}
              >
                <Icon
                  name={passwordPreviewEnabled ? 'hidden' : 'visible'}
                  color="gray"
                />
              </div>
            </Tooltip>
          </span>
        )}
      </div>
    );
  }
}

export default withTranslation(['components'], { withRef: true })(TextField);
